import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Levelselect.css';
import gsap from 'gsap';
import levelBg from '../asserts/levels.mp4';
import Header from '../components/layout/Header';
import Btn2 from '../components/common/Btn2';
import Btn3 from '../components/common/Btn3';
import Btn4 from '../components/common/Btn4';
import Preloader from '../components/layout/Preloader';
import fullHeartImg from '../asserts/fullheart.png'; 
import Btn1 from '../components/common/Btn1';
import { useMusic } from '../components/layout/MusicContext';

const STORAGE_KEY = 'unlockedLevels';

function getUnlocked() {
  const v = parseInt(sessionStorage.getItem(STORAGE_KEY), 10);
  return isNaN(v) ? 1 : Math.max(1, v);
}

export function unlockNextLevel(completedId) {
  const current = getUnlocked();
  const next = completedId + 1;
  if (next > current) sessionStorage.setItem(STORAGE_KEY, String(next));
}

const LEVELS = [
  { id: 1, label: 'Level 1', route: '/level1' },
  { id: 2, label: 'Level 2', route: '/level2' },
  { id: 3, label: 'Level 3', route: '/level3' },
  { id: 4, label: 'Level 4', route: '/level4' },
  { id: 5, label: 'Level 5', route: '/level5' },
  { id: 6, label: 'Level 6', route: '/level6' },
];

const HEART_SPARKLES = [
  { x: -150, y: -160, delay: 0.55, size: 26, color: '#ffe066' },
  { x:  140, y: -150, delay: 0.70, size: 20, color: '#ff9d3a' },
  { x: -170, y:   10, delay: 0.80, size: 22, color: '#ffffff' },
  { x:  160, y:   20, delay: 0.62, size: 24, color: '#ffe066' },
  { x:  -80, y:  170, delay: 0.88, size: 18, color: '#ff9d3a' },
  { x:   75, y:  175, delay: 0.68, size: 22, color: '#ffffff' },
  { x:   10, y: -190, delay: 0.75, size: 16, color: '#ffe066' },
  { x: -120, y:  -70, delay: 0.95, size: 14, color: '#ff9d3a' },
  { x:  125, y:  -65, delay: 0.90, size: 16, color: '#ffffff' },
  { x:  180, y:  -90, delay: 1.00, size: 20, color: '#ffe066' },
  { x: -180, y:  100, delay: 0.72, size: 18, color: '#ff9d3a' },
  { x:  130, y:  135, delay: 0.84, size: 24, color: '#ffffff' },
];

function HeartCompleteOverlay({ onClose }) {
  const overlayRef = useRef();
  const heartRef   = useRef();
  const titleRef   = useRef();
  const subRef     = useRef();
  const btnRef     = useRef();
  const sparkRefs  = useRef([]);
  const glowTween  = useRef(null);

  useLayoutEffect(() => {
    gsap.set(overlayRef.current, { opacity: 0 });
    gsap.set(heartRef.current,   { opacity: 0, scale: 0.3, y: 40 });
    gsap.set(titleRef.current,   { opacity: 0, y: -20 });
    gsap.set(subRef.current,     { opacity: 0, y: 10 });
    gsap.set(btnRef.current,     { opacity: 0, y: 20, scale: 0.85 });
    sparkRefs.current.forEach(el => el && gsap.set(el, { opacity: 0, scale: 0 }));

    const tl = gsap.timeline();
    tl.to(overlayRef.current, { opacity: 1, duration: 0.5, ease: 'power2.out' })
      .to(titleRef.current, { opacity: 1, y: 0, duration: 0.5, ease: 'back.out(2)' }, '-=0.1')
      .to(heartRef.current, { opacity: 1, scale: 1, y: 0, duration: 0.75, ease: 'back.out(2.2)' }, '-=0.1')
      .to(subRef.current, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, '-=0.2')
      .to(btnRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'back.out(2)' }, '-=0.1');

    HEART_SPARKLES.forEach((s, i) => {
      const el = sparkRefs.current[i];
      if (!el) return;
      tl.to(el, {
        opacity: 1, scale: 1, duration: 0.25, ease: 'back.out(2)',
        onComplete: () => {
          gsap.to(el, {
            opacity: 0, scale: 0.4,
            x: `+=${s.x > 0 ? 24 : -24}`,
            y: '-=40',
            duration: 0.6, ease: 'power2.out',
          });
        },
      }, s.delay);
    });

    glowTween.current = gsap.to(heartRef.current, {
      y: -16, duration: 1.3, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 1.2,
    });

    return () => { if (glowTween.current) glowTween.current.kill(); };
  }, []);

  const handleClose = () => {
    if (glowTween.current) glowTween.current.kill();
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.4, ease: 'power2.in', onComplete: onClose });
  };

  return (
    <div ref={overlayRef} className="heart-complete-overlay" onClick={handleClose}>
      <div className="heart-complete-wrap" onClick={e => e.stopPropagation()}>
        <h2 ref={titleRef} className="heart-complete-title">Mission Complete!</h2>

        <div ref={heartRef} className="heart-complete-heart">
          <img src={fullHeartImg} alt="The Pharaoh's Heart" className="heart-complete-img" />

          {HEART_SPARKLES.map((s, i) => (
            <div
              key={i}
              ref={el => sparkRefs.current[i] = el}
              className="heart-sparkle-star"
              style={{ left: `calc(50% + ${s.x}px)`, top: `calc(50% + ${s.y}px)`, width: s.size, height: s.size }}
            >
              <svg viewBox="0 0 20 20" width={s.size} height={s.size}>
                <polygon points="10,0 12,8 20,10 12,12 10,20 8,12 0,10 8,8" fill={s.color} />
              </svg>
            </div>
          ))}
        </div>

        <p ref={subRef} className="heart-complete-subtitle">
          You have gathered every fragment and restored the Pharaoh's heart.
        </p>

       
      </div>
    </div>
  );
}

const LevelSelect = () => {
  const [showPreloader, setShowPreloader] = useState(true);
  const [unlockedLevels, setUnlockedLevels] = useState(getUnlocked);
  const [toasts, setToasts] = useState([]);
  const [showHeartOverlay, setShowHeartOverlay] = useState(false);
  const pageRef = useRef();
  const btnRefs = useRef({});
  const navigate = useNavigate();
  const { playGiftMusic, stopResultMusic } = useMusic();

  const handlePreloaderDone = () => {
    const unlocked = getUnlocked();
    setUnlockedLevels(unlocked);
    if (unlocked > LEVELS.length) {
      setShowHeartOverlay(true);
      playGiftMusic();
    }
    setShowPreloader(false);
  };

  const handleLevelClick = (levelId, route) => {
    if (levelId > unlockedLevels) {
      const btn = btnRefs.current[levelId];
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const id = Date.now();
        setToasts(prev => [...prev, { id, x: rect.left + rect.width / 2, y: rect.top }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 1000);
      }
      return;
    }
    if (!route) return;
    navigate(route);
  };

  const handleRandom = () => {
    const unlocked = LEVELS.filter(lvl => lvl.id <= unlockedLevels);
    if (!unlocked.length) return;
    const picked = unlocked[Math.floor(Math.random() * unlocked.length)];
    navigate(picked.route);
  };

  useLayoutEffect(() => {
    if (showPreloader) return;
    const ctx = gsap.context(() => {
      gsap.set('.level-video',     { opacity: 0, filter: 'brightness(0.2) blur(14px)', scale: 1.04 });
      gsap.set('.level-btn-wrap',  { opacity: 0, y: 50, scale: 0.8 });
      gsap.set('.back-btn-wrap',   { opacity: 0, x: -40 });
      gsap.set('.random-btn-wrap', { opacity: 0, x: 40 });

      const tl = gsap.timeline();
      tl.to('.level-video',     { opacity: 1, filter: 'brightness(1) blur(0px)', scale: 1, duration: 2.2, ease: 'power3.out' })
        .to('.level-btn-wrap',  { opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.13, ease: 'back.out(2.4)' }, '-=0.8')
        .to('.back-btn-wrap',   { opacity: 1, x: 0, duration: 0.7, ease: 'back.out(2)' }, '-=0.3')
        .to('.random-btn-wrap', { opacity: 1, x: 0, duration: 0.7, ease: 'back.out(2)' }, '-=0.7');
    }, pageRef);
    return () => ctx.revert();
  }, [showPreloader]);

  if (showPreloader) return <Preloader onComplete={handlePreloaderDone} />;

  return (
    <div ref={pageRef} className="level-select">
      <div className="level-scroll-area">
        <video className="level-video" src={levelBg} autoPlay loop muted playsInline />
        <Header />

        <div className="levels-container">
          {LEVELS.map((lvl) => {
            const locked = lvl.id > unlockedLevels;
            return (
              <div
                key={lvl.id}
                ref={el => btnRefs.current[lvl.id] = el}
                className={`level-btn-wrap level-btn-${lvl.id} ${locked ? 'level-btn-locked' : ''}`}
                onClick={() => handleLevelClick(lvl.id, lvl.route)}
              >
                <Btn2 btnt={lvl.label} />
              </div>
            );
          })}
        </div>

        <div className="back-btn-wrap">
          <Btn3 onClick={() => navigate('/mainmenu')} />
        </div>

        <div className="random-btn-wrap">
          <Btn4 label="Random" onClick={handleRandom} />
        </div>
      </div>

      {toasts.map(toast => (
        <Toast key={toast.id} x={toast.x} y={toast.y} />
      ))}

      {showHeartOverlay && (
        <HeartCompleteOverlay onClose={() => { setShowHeartOverlay(false); stopResultMusic(); }} />
      )}
    </div>
  );
};

function Toast({ x, y }) {
  const ref = useRef();
  useLayoutEffect(() => {
    if (!ref.current) return;
    gsap.fromTo(ref.current, { opacity: 0, y: 0 }, { opacity: 1, y: -80, duration: 1.6, ease: 'power2.out' });
    gsap.to(ref.current, { opacity: 0, duration: 0.5, delay: 1.3, ease: 'power2.in' });
  }, []);
  return (
    <div ref={ref} className="locked-toast" style={{ left: x, top: y }}>
      This level is locked!
    </div>
  );
}

export default LevelSelect;